import React, { useState, useEffect } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import { 
  Search,
  Filter,
  X,
  Calendar,
  Download,
  Eye,
  FileText,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader,
  Users,
  TrendingUp,
  CreditCard,
  Package,
  UserPlus,
  Gift
} from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const DocPurchaseHistory = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isDocOpen, setIsDocOpen] = useState(false);
  
  // State for purchases
  const [purchases, setPurchases] = useState([]);
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for custom access modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedDocument, setSelectedDocument] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [isGrantingAccess, setIsGrantingAccess] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [searchDocument, setSearchDocument] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, success, pending, failed
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Stats
  const [stats, setStats] = useState({
    totalSales: 0,
    totalOrders: 0,
    successfulOrders: 0,
    pendingOrders: 0,
    failedOrders: 0,
    customAccessOrders: 0
  });

  // Fetch purchase history
  const fetchPurchaseHistory = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      console.log('Fetching from:', `${apiUrl}/api/pdf/admin/purchases`);
      const response = await axios.get(`${apiUrl}/api/pdf/admin/purchases`);
      
      console.log('Response:', response.data);
      
      if (response.data.success) {
        setPurchases(response.data.data);
        setFilteredPurchases(response.data.data);
        calculateStats(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load purchase history');
      }
    } catch (err) {
      console.error('Error fetching purchases:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to load purchase history. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch users for dropdown
  const fetchUsers = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/pdf/admin/users`);
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Fetch documents for dropdown
  const fetchDocuments = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/pdf`);
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  // Grant custom access
  const handleGrantAccess = async () => {
    if (!selectedUser || !selectedDocument) {
      setModalError('Please select both user and document');
      return;
    }
    
    setIsGrantingAccess(true);
    setModalError('');
    setModalSuccess('');
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const amount = customAmount && customAmount !== '' ? parseFloat(customAmount) : null;
      
      const response = await axios.post(`${apiUrl}/api/pdf/admin/grant-access`, {
        user_id: selectedUser,
        document_id: selectedDocument,
        amount: amount
      });
      
      if (response.data.success) {
        setModalSuccess('Custom access granted successfully!');
        setTimeout(() => {
          setIsModalOpen(false);
          resetModal();
          fetchPurchaseHistory(); // Refresh the list
        }, 2000);
      } else {
        setModalError(response.data.message || 'Failed to grant access');
      }
    } catch (err) {
      console.error('Error granting access:', err);
      setModalError(err.response?.data?.message || 'Failed to grant access. Please try again.');
    } finally {
      setIsGrantingAccess(false);
    }
  };

  // Reset modal state
  const resetModal = () => {
    setSelectedUser('');
    setSelectedDocument('');
    setCustomAmount('');
    setModalError('');
    setModalSuccess('');
    setSearchUser('');
    setSearchDocument('');
  };

  // Get document price when document is selected
  const getDocumentPrice = (docId) => {
    const doc = documents.find(d => d.id === parseInt(docId));
    return doc ? doc.price : 0;
  };

  // Handle document selection
  useEffect(() => {
    if (selectedDocument) {
      const price = getDocumentPrice(selectedDocument);
      setCustomAmount(price.toString());
    } else {
      setCustomAmount('');
    }
  }, [selectedDocument, documents]);

  // Filter users based on search
  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(searchUser.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchUser.toLowerCase()) ||
    user.phone?.includes(searchUser)
  );

  // Filter documents based on search
  const filteredDocuments = documents.filter(doc => 
    doc.title?.toLowerCase().includes(searchDocument.toLowerCase())
  );

  // Calculate statistics
  const calculateStats = (data) => {
    const successful = data.filter(p => p.payment_status === 'success').length;
    const pending = data.filter(p => p.payment_status === 'pending').length;
    const failed = data.filter(p => p.payment_status === 'failed').length;
    const customAccess = data.filter(p => p.is_custom_access === 1).length;
    const totalSales = data.reduce((sum, p) => sum + (p.payment_status === 'success' ? parseFloat(p.amount) : 0), 0);
    
    setStats({
      totalSales: totalSales,
      totalOrders: data.length,
      successfulOrders: successful,
      pendingOrders: pending,
      failedOrders: failed,
      customAccessOrders: customAccess
    });
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...purchases];
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(purchase => 
        purchase.document_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        purchase.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        purchase.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        purchase.razorpay_order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        purchase.razorpay_payment_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(purchase => purchase.payment_status === statusFilter);
    }
    
    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(now.setDate(now.getDate() - 7));
      const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
      
      filtered = filtered.filter(purchase => {
        const purchaseDate = new Date(purchase.purchased_at || purchase.created_at);
        if (dateFilter === 'today') {
          return purchaseDate >= today;
        } else if (dateFilter === 'week') {
          return purchaseDate >= weekAgo;
        } else if (dateFilter === 'month') {
          return purchaseDate >= monthAgo;
        }
        return true;
      });
    }
    
    setFilteredPurchases(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter, purchases]);

  useEffect(() => {
    fetchPurchaseHistory();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchPurchaseHistory, 30000);
    return () => clearInterval(interval);
  }, []);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status, isCustomAccess) => {
    if (isCustomAccess) {
      return {
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        text: 'text-purple-800 dark:text-purple-300',
        icon: <Gift className="w-4 h-4" />,
        label: 'Custom Access'
      };
    }
    
    switch(status) {
      case 'success':
        return {
          bg: 'bg-green-100 dark:bg-green-900/30',
          text: 'text-green-800 dark:text-green-300',
          icon: <CheckCircle className="w-4 h-4" />,
          label: 'Success'
        };
      case 'pending':
        return {
          bg: 'bg-yellow-100 dark:bg-yellow-900/30',
          text: 'text-yellow-800 dark:text-yellow-300',
          icon: <Clock className="w-4 h-4" />,
          label: 'Pending'
        };
      case 'failed':
        return {
          bg: 'bg-red-100 dark:bg-red-900/30',
          text: 'text-red-800 dark:text-red-300',
          icon: <AlertCircle className="w-4 h-4" />,
          label: 'Failed'
        };
      default:
        return {
          bg: 'bg-gray-100 dark:bg-gray-800',
          text: 'text-gray-800 dark:text-gray-300',
          icon: <AlertCircle className="w-4 h-4" />,
          label: status
        };
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFilter('all');
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPurchases.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Order ID', 'Document Title', 'User Name', 'User Email', 'Amount', 'Payment Status', 'Purchase Date', 'Payment ID', 'Access Type'];
    const csvData = filteredPurchases.map(p => [
      p.razorpay_order_id || 'N/A',
      p.document_title,
      p.user_name || 'N/A',
      p.user_email || 'N/A',
      formatCurrency(p.amount),
      p.is_custom_access ? 'Custom Access' : p.payment_status,
      formatDate(p.purchased_at || p.created_at),
      p.razorpay_payment_id || 'N/A',
      p.is_custom_access ? 'Custom Grant' : 'Payment'
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase_history_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Handle body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [sidebarOpen]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="flex h-screen">
        <Sidebar isOpen={sidebarOpen} />
        
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Page Header */}
            <div className="flex flex-wrap gap-4 mb-6 md:mb-8">
              <div className="flex-1">
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                  Document Purchase History
                </h1>
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
                  Track and manage all document purchases
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setIsModalOpen(true);
                    fetchUsers();
                    fetchDocuments();
                  }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Give Custom Access
                </button>
                <Link to="/admin/upload-pdf" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Manage Documents
                </Link>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Sales</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(stats.totalSales)}</p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Orders</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalOrders}</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Successful</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.successfulOrders}</p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.pendingOrders}</p>
                  </div>
                  <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Failed</p>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.failedOrders}</p>
                  </div>
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Custom Access</p>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.customAccessOrders}</p>
                  </div>
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <Gift className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Filters Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search Bar */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by document title, user name, email, order ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700"
                    />
                  </div>
                </div>
                
                {/* Status Filter */}
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                  >
                    <option value="all">All Status</option>
                    <option value="success">Success</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                
                {/* Date Filter */}
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>
                
                {/* Export Button */}
                <button
                  onClick={exportToCSV}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
                
                {/* Clear Filters */}
                {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Clear Filters
                  </button>
                )}
              </div>
              
              {/* Results Count */}
              <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                Found {filteredPurchases.length} purchase{filteredPurchases.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader className="w-8 h-8 text-blue-600 animate-spin" />
                <span className="ml-2 text-gray-600">Loading purchase history...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-red-700 dark:text-red-300">{error}</p>
                <button
                  onClick={fetchPurchaseHistory}
                  className="ml-auto px-3 py-1 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 transition"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Purchases Table */}
            {!isLoading && !error && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">#</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Order ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Document</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Purchase Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Payment ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {currentItems.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                            <p>No purchases found</p>
                            <button
                              onClick={clearFilters}
                              className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
                            >
                              Clear filters
                            </button>
                          </td>
                        </tr>
                      ) : (
                        currentItems.map((purchase, index) => {
                          const status = getStatusBadge(purchase.payment_status, purchase.is_custom_access);
                          const serialNumber = indexOfFirstItem + index + 1;
                          return (
                            <tr key={purchase.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {serialNumber}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-gray-100">
                                {purchase.razorpay_order_id ? purchase.razorpay_order_id.substring(0, 15) + '...' : (purchase.is_custom_access ? 'Custom Grant' : 'N/A')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {purchase.document_title}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {purchase.user_name || 'N/A'}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {purchase.user_email || 'N/A'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-1">
                                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {formatCurrency(purchase.amount)}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                                  {status.icon}
                                  {status.label}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {formatDate(purchase.purchased_at || purchase.created_at)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                                  {purchase.razorpay_payment_id ? purchase.razorpay_payment_id.substring(0, 15) + '...' : (purchase.is_custom_access ? 'Custom Grant' : 'N/A')}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredPurchases.length)} of {filteredPurchases.length} entries
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            className={`w-8 h-8 rounded-lg transition ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </main>

          <Footer />
        </div>
      </div>

      {/* Custom Access Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Gift className="w-5 h-5 text-purple-600" />
                  Grant Custom Access
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    resetModal();
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
















               {/* User Selection */}
{/* User Selection */}
<div className="relative">
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
    Select User *
  </label>
  
  <div className="relative">
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
    <input
      type="text"
      placeholder="Search user by name, email, or phone..."
      value={searchUser}
      onFocus={() => setIsOpen(true)} // Show dropdown when user clicks the input
      onChange={(e) => {
        setSearchUser(e.target.value);
        setIsOpen(true); // Keep it open while typing
      }}
      className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 text-sm"
    />
  </div>

  {/* Dropdown Menu - Controlled by isOpen and searchUser length */}
  {isOpen && searchUser.length > 0 && (
    <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
      {filteredUsers.length > 0 ? (
        filteredUsers.map((user) => (
          <div
            key={user.id}
            className="px-4 py-3 cursor-pointer hover:bg-purple-50 dark:hover:bg-gray-700 border-b last:border-b-0 border-gray-100 dark:border-gray-600 transition-colors"
            onClick={() => {
              setSelectedUser(user.id);     // Store the ID for your form
              setSearchUser(user.username); // Update input text to show the name
              setIsOpen(false);             // <--- THIS HIDES THE DROPDOWN
            }}
          >
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {user.username}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {user.email} • {user.phone}
            </div>
          </div>
        ))
      ) : (
        <div className="px-4 py-3 text-sm text-gray-500 italic">No matches found</div>
      )}
    </div>
  )}
</div>
                
                {/* Document Selection */}
{/* Document Selection */}
<div className="relative"> {/* Outer div must be relative for the dropdown to align */}
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
    Select Document *
  </label>
  
  <div className="relative">
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
    <input
      type="text"
      placeholder="Search document by title..."
      value={searchDocument}
      onFocus={() => setIsDocOpen(true)} // Show when clicked
      onChange={(e) => {
        setSearchDocument(e.target.value);
        setIsDocOpen(true);
      }}
      className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700"
    />
  </div>

  {/* Floating Results Menu */}
  {isDocOpen && searchDocument && (
    <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl max-h-60 overflow-auto">
      {filteredDocuments.length > 0 ? (
        filteredDocuments.map((doc) => (
          <div
            key={doc.id}
            className="px-4 py-2 cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900 text-sm border-b last:border-0 border-gray-100 dark:border-gray-700"
            onClick={() => {
              setSelectedDocument(doc.id);     // Sets the ID for your logic
              setSearchDocument(doc.title);    // Shows title in the search box
              setIsDocOpen(false);             // Hides the dropdown immediately
            }}
          >
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {doc.title}
            </div>
            <div className="text-xs text-gray-500">
              Price: {formatCurrency(doc.price)}
            </div>
          </div>
        ))
      ) : (
        <div className="px-4 py-2 text-sm text-gray-500 italic">No documents found</div>
      )}
    </div>
  )}
</div>
                
                {/* Amount (Editable) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Amount (Optional - Editable)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="Enter amount (leave empty for document price)"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    If left empty, the document's original price will be used
                  </p>
                </div>
                
                {/* Error Message */}
                {modalError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <p className="text-sm text-red-700 dark:text-red-300">{modalError}</p>
                  </div>
                )}
                
                {/* Success Message */}
                {modalSuccess && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <p className="text-sm text-green-700 dark:text-green-300">{modalSuccess}</p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      resetModal();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGrantAccess}
                    disabled={isGrantingAccess || !selectedUser || !selectedDocument}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isGrantingAccess ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Granting...
                      </>
                    ) : (
                      <>
                        <Gift className="w-4 h-4" />
                        Grant Access
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocPurchaseHistory;